import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Trophy, Loader2, Heart, Star, Award, Target } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CAT_STYLES = {
  estrela: { icon: Star, label: 'Estrela', emoji: '⭐' },
  top: { icon: Trophy, label: 'Top', emoji: '🏆' },
  marco: { icon: Target, label: 'Marco', emoji: '🎯' },
};

// CSS-only confetti renderer (no external deps)
const Confetti = () => {
  const colors = ['#10b981', '#34d399', '#fbbf24', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'];
  const pieces = Array.from({ length: 60 });
  return (
    <div className="pointer-events-none fixed inset-0 z-[10000] overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 1.5;
        const duration = 2.5 + Math.random() * 2;
        const size = 6 + Math.random() * 8;
        const color = colors[i % colors.length];
        const rotation = Math.random() * 360;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              top: '-20px',
              width: `${size}px`,
              height: `${size * 1.6}px`,
              backgroundColor: color,
              transform: `rotate(${rotation}deg)`,
              animation: `confetti-fall ${duration}s ${delay}s linear infinite`,
              opacity: 0.9,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export const RecognitionBlockingPopup = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [acking, setAcking] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/admin-alerts/pending`);
      const all = Array.isArray(res.data) ? res.data : [];
      setPending(all.filter(a => (a.kind || 'alert') === 'recognition'));
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, 20000);
    return () => clearInterval(t);
  }, [fetchPending]);

  if (!user || pending.length === 0) return null;
  const current = pending[0];
  const cat = CAT_STYLES[current.severity] || CAT_STYLES.estrela;
  const Icon = cat.icon;

  const handleAck = async () => {
    setAcking(true);
    try {
      await axios.post(`${API_URL}/admin-alerts/${current.id}/ack`);
      toast.success('Obrigado!');
      setPending(prev => prev.filter(a => a.id !== current.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro');
    } finally {
      setAcking(false);
    }
  };

  return (
    <>
      <Confetti />
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
        style={{ background: 'rgba(6, 78, 59, 0.85)', backdropFilter: 'blur(6px)' }}
        data-testid="recognition-blocking-popup"
      >
        <div className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/30 animate-in zoom-in-95 duration-500">
          {/* Pulsing header */}
          <div className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600 px-6 py-6 flex items-center gap-4">
            <div className="relative flex items-center justify-center">
              <span className="animate-ping absolute inline-flex h-10 w-10 rounded-full bg-white/40 opacity-75" />
              <Icon className="w-9 h-9 text-white relative" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-white/80 font-bold">{cat.emoji} Reconhecimento · {cat.label}</p>
              <p className="text-lg font-bold text-white truncate">{current.sender_name} reconheceu o teu trabalho</p>
            </div>
            {pending.length > 1 && (
              <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                1 de {pending.length}
              </span>
            )}
          </div>

          <div className="bg-white px-6 py-8 space-y-6">
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-5">
              <p className="text-lg text-slate-800 whitespace-pre-wrap leading-relaxed font-medium text-center">
                {current.message}
              </p>
            </div>

            <Button
              onClick={handleAck}
              disabled={acking}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 gap-2 h-14 text-lg font-bold shadow-lg"
              data-testid="recognition-ack-btn"
            >
              {acking ? <Loader2 className="w-6 h-6 animate-spin" /> : <Heart className="w-6 h-6 fill-current" />}
              Obrigado! 🙌
            </Button>

            <p className="text-xs text-center text-slate-400">
              Este reconhecimento ficou registado na tua coleção pessoal de troféus.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecognitionBlockingPopup;
